// @ts-check

/**
 * @typedef {Record<string, FormDataEntryValue | FormDataEntryValue[]>} Data
 */

class FormPersistence extends HTMLElement {
	/** @type {HTMLFormElement | undefined} */
	form;

	/** @type {{ persist: string; persisted?: boolean; persistedError?: boolean; persistedOnNoData?: string } | undefined} */
	options;

	connectedCallback() {
		this.form = this.querySelector('form') ?? undefined;

		if (!this.form) {
			console.warn('Component requires form element');

			return;
		}

		// Read options from contained form
		this.options = this.form.dataset.formOptions
			? JSON.parse(this.form.dataset.formOptions)
			: undefined;

		if (!this.options) {
			console.warn('Component requires form options');

			return;
		}

		const storageKey = this.options.persist;

		// Case 1: Write
		if (!this.options.persisted) {
			this.form.addEventListener('submit', () => {
				const formData = new FormData(this.form);
				/** @type {Data} */
				const data = {};

				for (const [key, value] of formData.entries()) {
					if (['_csrf'].includes(key)) {
						continue;
					}

					if (!value) {
						continue;
					}

					if (data[key]) {
						if (!Array.isArray(data[key])) {
							data[key] = [data[key]];
						}

						data[key].push(value);
					} else {
						data[key] = value;
					}
				}

				localStorage.setItem(storageKey, JSON.stringify(data));
			});
		}

		// Case 2: Read
		else {
			const persistedData = localStorage.getItem(storageKey);

			if (!persistedData) {
				console.warn('No persisted data found');

				this.handleNoData();

				return;
			}

			const data = JSON.parse(persistedData);

			if (this.options.persistedError) {
				this.handleServerError(data);

				return;
			}

			for (const [key, originalValue] of Object.entries(data)) {
				const values = Array.isArray(originalValue) ? originalValue : [originalValue];

				for (const value of values) {
					const input = document.createElement('input');

					input.type = 'hidden';
					input.name = key;
					input.value = value;

					this.form.appendChild(input);
				}
			}
		}
	}

	/**
	 * @param {Data} data
	 */
	handleServerError(data) {
		/** @type {HTMLTextAreaElement | undefined} */
		const textarea = this.form?.querySelector('[name="debug"]') ?? undefined;

		if (!textarea) {
			console.warn('No textarea found');

			return;
		}

		textarea.value = JSON.stringify(data, null, 2);

		// Move out of form to prevent it from being submitted
		/** @type {HTMLElement} */
		const element =
			textarea.closest('.ln-form__group') ||
			textarea.closest('[data-form="field"]') ||
			textarea;

		setTimeout(() => {
			this.appendChild(element);
		}, 0);
	}

	handleNoData() {
		const error =
			this.options?.persistedOnNoData ??
			'An error occurred. Please contact support@legacynotes.ch';
		const errorElement = document.createElement('div');

		errorElement.classList.add(
			'ln-form__group',
			'ln-layout-section',
			'ln-layout-section--blank'
		);
		errorElement.innerHTML = `<div class="ln-layout-content__inner"><p class="ln-form__row ln-form__row--text ln-form__error">${error}</p></div>`;

		this.form?.setAttribute('hidden', 'true');

		this.append(errorElement);
	}
}

customElements.define('ln-form-persistence', FormPersistence);
